import React, { useEffect, useState } from 'react';
import { LockIcon, BellIcon, EyeIcon, EyeOffIcon, AlertTriangleIcon, CheckCircleIcon, Loader2Icon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
export const SecuritySection = () => {
  const {
    user,
    auth
  } = useAuth();
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  // Security alerts state
  const [securityAlerts, setSecurityAlerts] = useState({
    suspiciousLogin: true,
    passwordChanged: true
  });
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingAlerts, setIsSavingAlerts] = useState(false);
  const [alertsError, setAlertsError] = useState('');
  const [alertsSuccess, setAlertsSuccess] = useState(false);
  // Fetch user security settings
  useEffect(() => {
    const fetchSecuritySettings = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      try {
        const securityDocRef = doc(db, 'userSecurity', user.id);
        const securityDoc = await getDoc(securityDocRef);
        if (securityDoc.exists()) {
          const data = securityDoc.data();
          if (data.securityAlerts) {
            setSecurityAlerts(data.securityAlerts);
          }
        } else {
          const defaultSettings = {
            securityAlerts: {
              suspiciousLogin: true,
              passwordChanged: true
            },
            createdAt: new Date(),
            userId: user.id
          };
          await setDoc(securityDocRef, defaultSettings);
        }
      } catch (err) {
        console.error('Error fetching security settings:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSecuritySettings();
  }, [user]);
  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) {
      setPasswordError('You must be logged in to change your password');
      return;
    }
    setPasswordError('');
    setPasswordSuccess(false);
    setIsChangingPassword(true);
    // Validate password
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      setIsChangingPassword(false);
      return;
    }
    if (!/(?=.*[0-9])/.test(newPassword)) {
      setPasswordError('Password must contain at least one number');
      setIsChangingPassword(false);
      return;
    }
    if (!/(?=.*[!@#$%^&*])/.test(newPassword)) {
      setPasswordError('Password must contain at least one special character');
      setIsChangingPassword(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      setIsChangingPassword(false);
      return;
    }
    try {
      // Reauthenticate the user first
      const credential = EmailAuthProvider.credential(auth.currentUser.email || '', currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      // Then update the password
      await updatePassword(auth.currentUser, newPassword);
      // Log the password change in the security document
      if (user?.id) {
        const securityDocRef = doc(db, 'userSecurity', user.id);
        const securityDoc = await getDoc(securityDocRef);
        if (securityDoc.exists()) {
          await updateDoc(securityDocRef, {
            lastPasswordChange: new Date(),
            updatedAt: new Date()
          });
        }
        // Add an entry to the security log
        const securityLogRef = doc(db, 'securityLogs', `password-change-${Date.now()}`);
        await setDoc(securityLogRef, {
          userId: user.id,
          action: 'password-change',
          timestamp: new Date(),
          ipAddress: '127.0.0.1',
          userAgent: navigator.userAgent
        });
      }
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else if (error.code === 'auth/too-many-requests') {
        setPasswordError('Too many attempts. Please try again later');
      } else if (error.code === 'auth/requires-recent-login') {
        setPasswordError('Please log out and log back in before changing your password');
      } else {
        setPasswordError(`Failed to change password: ${error.message}`);
      }
    } finally {
      setIsChangingPassword(false);
    }
  };
  // Handle security alert toggle
  const handleSecurityAlertToggle = async (alert: 'suspiciousLogin' | 'passwordChanged') => {
    if (!user?.id) return;
    setIsSavingAlerts(true);
    setAlertsError('');
    setAlertsSuccess(false);
    try {
      const newAlerts = {
        ...securityAlerts,
        [alert]: !securityAlerts[alert]
      };
      setSecurityAlerts(newAlerts);
      const securityDocRef = doc(db, 'userSecurity', user.id);
      await updateDoc(securityDocRef, {
        securityAlerts: newAlerts,
        updatedAt: new Date()
      });
      setAlertsSuccess(true);
    } catch (error) {
      console.error('Error updating security alert settings:', error);
      setAlertsError('Failed to update alert settings. Please try again.');
      // Revert the UI state if the update fails
      setSecurityAlerts({
        ...securityAlerts,
        [alert]: !securityAlerts[alert]
      });
    } finally {
      setIsSavingAlerts(false);
    }
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-[300px]" data-id="element-3558">
        <Loader2Icon className="h-8 w-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3559" />
        <span className="ml-2 text-gray-600 dark:text-[#B0C1BD]" data-id="element-3560">
          Loading security settings...
        </span>
      </div>;
  }
  return <div data-id="element-3561">
      <div className="flex justify-between items-center mb-6" data-id="element-3562">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3563">
          Security & Login
        </h2>
      </div>
      <div className="space-y-8" data-id="element-3564">
        {/* Change Password Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3565">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3566">
            <div className="flex items-center" data-id="element-3567">
              <LockIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3568" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3569">
                Change Password
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3570">
            {passwordSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-3571">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3572" />
                <p data-id="element-3573">Your password has been successfully updated.</p>
              </div>}
            {passwordError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md flex items-start" data-id="element-3574">
                <AlertTriangleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3575" />
                <p data-id="element-3576">{passwordError}</p>
              </div>}
            <form onSubmit={handlePasswordChange} data-id="element-3577">
              <div className="space-y-4" data-id="element-3578">
                <div data-id="element-3579">
                  <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3580">
                    Current Password
                  </label>
                  <div className="relative" data-id="element-3581">
                    <input id="current-password" type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3582" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowCurrentPassword(!showCurrentPassword)} data-id="element-3583">
                      {showCurrentPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3584" /> : <EyeIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3585" />}
                    </button>
                  </div>
                </div>
                <div data-id="element-3586">
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3587">
                    New Password
                  </label>
                  <div className="relative" data-id="element-3588">
                    <input id="new-password" type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3589" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowNewPassword(!showNewPassword)} data-id="element-3590">
                      {showNewPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3591" /> : <EyeIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3592" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3593">
                    Password must be at least 8 characters and include a number
                    and special character.
                  </p>
                </div>
                <div data-id="element-3594">
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3595">
                    Confirm New Password
                  </label>
                  <div className="relative" data-id="element-3596">
                    <input id="confirm-password" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3597" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-3598">
                      {showConfirmPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3599" /> : <EyeIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3600" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-3601">
                  <button type="submit" className="px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" disabled={isChangingPassword} data-id="element-3602">
                    {isChangingPassword ? <>
                        <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3603" />
                        Saving...
                      </> : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </section>

        {/* Security Alerts Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3604">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3605">
            <div className="flex items-center" data-id="element-3606">
              <BellIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3607" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3608">
                Security Alerts
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3609">
            {alertsSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-3610">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3611" />
                <p data-id="element-3612">Your alert preferences have been updated.</p>
              </div>}
            {alertsError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md flex items-start" data-id="element-3613">
                <AlertTriangleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3614" />
                <p data-id="element-3615">{alertsError}</p>
              </div>}
            <div className="space-y-4" data-id="element-3616">
              <div className="flex items-center justify-between" data-id="element-3617">
                <div data-id="element-3618">
                  <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3619">
                    Suspicious login activity
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3620">
                    Get notified when someone logs in from an unrecognized
                    device or location
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-3621">
                  <input type="checkbox" className="sr-only peer" checked={securityAlerts.suspiciousLogin} onChange={() => handleSecurityAlertToggle('suspiciousLogin')} disabled={isSavingAlerts} data-id="element-3622" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3623"></div>
                  {isSavingAlerts && <Loader2Icon className="ml-2 h-4 w-4 text-gray-500 dark:text-[#7A8F8A] animate-spin" data-id="element-3624" />}
                </label>
              </div>
              <div className="flex items-center justify-between" data-id="element-3625">
                <div data-id="element-3626">
                  <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3627">
                    Password changes
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3628">
                    Get notified when your password is changed
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-3629">
                  <input type="checkbox" className="sr-only peer" checked={securityAlerts.passwordChanged} onChange={() => handleSecurityAlertToggle('passwordChanged')} disabled={isSavingAlerts} data-id="element-3630" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3631"></div>
                </label>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>;
};